import {
  CourseDeliveryDTO,
  CourseSetDTO,
  CourseSetStatusSummaryForSiteResponse,
  CustomerDTO,
  SiteDTO,
} from "@beable/roster-backend-client-v1";
import {
  Box,
  Button,
  CircularProgress,
  Container,
  FormControl,
  FormControlLabel,
  Grid,
  Radio,
  RadioGroup,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { format } from "date-fns-tz";
import { isEqual } from "lodash";
import React, { useCallback, useEffect, useState } from "react";
import courseService from "../../../services/CourseService";
import ContentCourseSelector from "../../Common/Selectors/ContentCourseSelector";
import CourseSetSelector from "../../Common/Selectors/CourseSetSelector";
import { getStyles } from "./styles";

enum ProvisionByType {
  Course = "Course",
  CourseSet = "CourseSet",
}

interface CourseDashboardProps {
  customer: CustomerDTO;
  site: SiteDTO;
  academicYearId: string;
  course?: CourseDeliveryDTO;
  courseSet?: CourseSetDTO;
  autoRefresh?: boolean;
}

export default function CourseDashboard({
  customer,
  site,
  academicYearId,
  course,
  courseSet,
  autoRefresh,
}: CourseDashboardProps) {
  const theme = useTheme();
  const styles = getStyles(theme);

  const [isLoading, setIsLoading] = useState(false);
  const [selectedCourse, setSelectedCourse] = useState<CourseDeliveryDTO | undefined>(course);
  const [selectedCourseSet, setSelectedCourseSet] = useState<CourseSetDTO | undefined>(courseSet);
  const [provisionType, setProvisionType] = useState<ProvisionByType>("" as ProvisionByType);

  const [stats, setStats] = useState<
    CourseSetStatusSummaryForSiteResponse | undefined
  >();

  const handleProvisionChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setProvisionType(event.target.value as ProvisionByType);
    setSelectedCourse(undefined);
    setSelectedCourseSet(undefined);
    setStats(undefined);
  };

  const getStats = useCallback(async () => {
    setIsLoading(true);
    let data: CourseSetStatusSummaryForSiteResponse | undefined;
    if (site.id && academicYearId && (selectedCourse || selectedCourseSet)) {
      data = await courseService.getCourseSetStatusSummaryForSite(
        site.id,
        academicYearId,
        selectedCourse?.courseId,
        selectedCourseSet?.id
      );
    }
    setStats(data);
    setIsLoading(false);
    return data;
  }, [site.id, academicYearId, selectedCourse, selectedCourseSet]);

  useEffect(() => {
    getStats();
  }, [getStats]);

  useEffect(() => {
    async function refreshStats(
      iteration: number,
      prevStats?: CourseSetStatusSummaryForSiteResponse
    ) {
      if (iteration > 20) {
        //don't check for more than 10 minutes, in case screen is idle
        return;
      }
      const newStats = await getStats();
      if (!isEqual(prevStats, newStats)) {
        setTimeout(
          async () => await refreshStats(++iteration, newStats),
          20000
        );
      }
    }

    if (autoRefresh) {
      refreshStats(0);
    }
  }, [autoRefresh, getStats]);

  const displayStats = () => {
    if (!stats) return;

    return (
      <Container sx={styles.metricsContainer}>
        <Box sx={[styles.metricsContent, styles.colorBlockA]}>
          <Typography gutterBottom variant="h6">
            Schedule
          </Typography>
          {selectedCourseSet ? (
            <Typography gutterBottom variant="body1">
              Courses: {stats?.totalCourses}
            </Typography>
          ) : (
            ``
          )}
          <Typography gutterBottom variant="body1">
            Lessons: {stats?.totalLessons}
          </Typography>
        </Box>
        <Box sx={[styles.metricsContent, styles.colorBlockB]}>
          <Typography gutterBottom variant="h6">
            Schools
          </Typography>
          <Typography gutterBottom variant="body1">
            {stats?.schools.siteLevel ? "1" : "0"} site level schedule(s)
          </Typography>
          <Typography gutterBottom variant="body1">
            {stats?.schools.total} school level schedule(s)
          </Typography>
          <Typography
            gutterBottom
            variant="body1"
            sx={{ textAlign: "center" }}
          >
            Last record added:
            {stats?.schools.lastCreatedDt ? (
              <>
                <br />
                {format(
                  new Date(stats?.schools.lastCreatedDt),
                  "MM/dd/yyyy HH:mm:ss"
                )}
              </>
            ) : (
              " N/A"
            )}
          </Typography>
        </Box>
        <Box sx={[styles.metricsContent, styles.colorBlockA]}>
          <Typography gutterBottom variant="h6">
            Classes
          </Typography>
          <Typography gutterBottom variant="body1">
            Assigned to: {stats?.classes.total} classes
          </Typography>
          <Typography
            gutterBottom
            variant="body1"
            sx={{ textAlign: "center" }}
          >
            Last record added:
            {stats?.classes.lastCreatedDt ? (
              <>
                <br />
                {format(
                  new Date(stats?.classes.lastCreatedDt),
                  "MM/dd/yyyy HH:mm:ss"
                )}
              </>
            ) : (
              " N/A"
            )}
          </Typography>
        </Box>
        <Box sx={[styles.metricsContent, styles.colorBlockB]}>
          <Typography gutterBottom variant="h6">
            Users
          </Typography>
          <Typography gutterBottom variant="body1">
            Assigned to: {stats?.users.total} users
          </Typography>
          <Typography gutterBottom variant="body1">
            {stats?.users.lessonsAssigned} total lessons assigned
          </Typography>
          <Typography
            gutterBottom
            variant="body1"
            sx={{ textAlign: "center" }}
          >
            Last record added:
            {stats?.users.lastCreatedDt ? (
              <>
                <br />
                {format(
                  new Date(stats?.users.lastCreatedDt),
                  "MM/dd/yyyy HH:mm:ss"
                )}
              </>
            ) : (
              " N/A"
            )}
          </Typography>
        </Box>
      </Container>
    );
  };
  const courseSelector = () => {
    if (course) {
        return (
          <Grid container size={5}>
            <Typography variant="h5">Course: {course.courseName}</Typography>
          </Grid>
        );
    }
    if (courseSet) {
        return (
          <Grid container size={5}>
            <Typography variant="h5">Course Set: {courseSet.name}</Typography>
          </Grid>
        );
    }
    return (
      <Grid container size={5}>
        <FormControl variant="standard">
          <RadioGroup
            row
            aria-label="provision by"
            value={provisionType}
            onChange={handleProvisionChange}
            sx={styles.radioGroup}
          >
            <FormControlLabel
              value={ProvisionByType.Course}
              control={<Radio />}
              label="Course"
            />
            <FormControlLabel
              value={ProvisionByType.CourseSet}
              control={<Radio />}
              label="Course Set"
            />
          </RadioGroup>
        </FormControl>
        {provisionType === ProvisionByType.Course && (
          <ContentCourseSelector
            courseId={selectedCourse?.courseId || ""}
            locationCode={customer.locationCode}
            onChange={setSelectedCourse}
          />
        )}
        {provisionType === ProvisionByType.CourseSet && (
          <CourseSetSelector
            courseSetId={selectedCourseSet?.id}
            locationCode={customer.locationCode}
            siteId={site.id}
            onChange={setSelectedCourseSet}
          />
        )}
      </Grid>
    );
  }
  return (
    <div>
      { courseSelector() }
      <Grid
        container
        sx={[{
          justifyContent: "center"
        }, styles.content]}>
        <Box sx={{ paddingBottom: "10px" }}>
          <Button
            onClick={getStats}
            sx={{ width: "30px !important" }}
            variant="contained"
            color="secondary"
          >
            REFRESH
          </Button>
        </Box>
        {isLoading ? <CircularProgress color="primary" /> : displayStats()}
      </Grid>
    </div>
  );
}